import React from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { Skeleton } from '@material-ui/lab';
import { Box, Icon, IconButton } from '@popmenu/common-ui';
import { X as XIcon } from '@popmenu/web-icons';
import { makeStyles } from '../../../utils/withStyles';
import { useMenuItemData } from '../../hooks/data/useMenuItemData';
import { usePopmenuConfig } from '../../hooks/usePopmenuConfig';
import { useRouteParams } from '../../hooks/useRouteParams';
import { dishModalFullscreenBreakpoint } from './dishModalFullscreenBreakpoint';
import BasicModal from '../../../admin/shared/BasicModal';
import CustomImg from '../../../shared/CustomImg';
import { DishModalAvatars } from '../DishModalAvatars';
import { DishLikesButton } from '../DishLikesButton';
import { DishModalCTA } from '../DishModalCTA';
import { DishModalDishDetails } from '../DishModalDishDetails';
import { DishModalDishTitle } from '../DishModalDishTitle';
import { DishShareButton } from '../DishShareButton';
import { DishModalReviews } from '../DishModalReviews';
import { Meta } from '../Meta';

const useStyles = makeStyles(theme => ({
  closeButton: {
    backgroundColor: theme.palette.surface.light,
    border: `2px solid ${theme.palette.text.primary}`,
    fontSize: '1rem',
    padding: theme.spacing(0.5),
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  container: {
    backgroundColor: theme.palette.surface.light,
    borderRadius: theme.spacing(1),
    [dishModalFullscreenBreakpoint]: {
      minHeight: '100vh',
      paddingBottom: '85px',
    },
    position: 'relative',
  },
  detailsSkeleton: {
    fontSize: '1.25rem',
    lineHeight: 'normal',
    width: '100%',
  },
  infoContainer: {
    padding: theme.spacing(2),
  },
  nameSkeleton: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
    fontSize: '2rem',
    lineHeight: 'normal',
    width: '66%',
  },
  photo: {
    borderRadius: theme.spacing(1, 1, 0, 0),
    height: '100%',
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  photoContainer: {
    backgroundColor: theme.palette.gray.main,
    borderRadius: theme.spacing(1, 1, 0, 0),
    height: 0,
    marginBottom: theme.spacing(0.5),
    overflow: 'hidden',
    paddingTop: '80%',
    position: 'relative',
  },
  photoSkeleton: {
    borderRadius: theme.spacing(1, 1, 0, 0),
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
}));

export const DishModal = () => {
  const classes = useStyles();
  const history = useHistory();
  const { eatPopmenuUrl } = usePopmenuConfig();
  const params = useRouteParams();
  const dishSlug = params.dishSlug ?? '';
  const dishListFriendlySlug = params.dishListSlug ?? '';
  const placeSlug = params.placeSlug ?? '';
  const categorySlug = params.categorySlug ?? null;

  const dishId = dishSlug ? parseInt(dishSlug.split('-')[0] ?? '', 10) : null;
  const dishListSlug = dishListFriendlySlug ? dishListFriendlySlug.split('-')[0] : null;
  const { data: dish } = useMenuItemData({
    dishListSlug,
    menuItemId: dishId,
    rootPlaceSlug: placeSlug,
  });

  let returnPath: string;
  if (dishListSlug) {
    returnPath = `/lists/${dishListFriendlySlug}`;
  } else if (categorySlug) {
    returnPath = `/places/${placeSlug}/categories/${categorySlug}`;
  } else {
    returnPath = `/places/${placeSlug}`;
  }
  const dishPath = `${returnPath}/dishes/${dish?.idAsSlug || dishSlug}`;
  const shareUrl = `${eatPopmenuUrl}${dishPath}`;
  const actionText = dish?.location?.eatPopCtaText;
  const actionUrl = dish?.location?.eatPopCtaLinkUrl || dish?.restaurant?.popmenuUrl;

  const closeModal = () => {
    history.push(returnPath);
  };

  // After loading dish, ensure slug in URL matches expected slug
  if (dish && dish.idAsSlug !== dishSlug) {
    return (
      <Redirect to={dishPath} />
    );
  }

  return (
    <BasicModal
      closeButton={false}
      closeModal={closeModal}
      disableBackdropClick={false}
      disablePortal
      disableTransition
      fullScreenMobile
      paper={false}
      show
      size="md"
    >
      <Box className={classes.container}>
        {!(dish?.restaurant && dish?.location) && (
          <React.Fragment>
            <Box className={classes.photoContainer}>
              <Skeleton className={classes.photoSkeleton} variant="rect" />
            </Box>
            <Box className={classes.infoContainer}>
              <Skeleton className={classes.nameSkeleton} />
              <Skeleton className={classes.nameSkeleton} />
              <Skeleton className={classes.detailsSkeleton} />
              <Skeleton className={classes.detailsSkeleton} />
              <Skeleton className={classes.detailsSkeleton} />
            </Box>
          </React.Fragment>
        )}
        {dish?.restaurant && dish?.location && (
          <React.Fragment>
            <Meta
              canonicalPath={dishPath}
              description={`${dish.displayName} from ${dish.restaurant.name} in ${dish.location?.city}, ${dish.location?.state || dish.location?.countryName}. ${dish.generatedDescription || ''}`.trim()}
              imageHeight={dish.featuredPhoto?.shareUrl ? 630 : undefined}
              imageUrl={dish.featuredPhoto?.shareUrl ?? undefined}
              imageWidth={dish.featuredPhoto?.shareUrl ? 1200 : undefined}
              title={`${dish.displayName} in ${dish.location?.city}, ${dish.location?.state || dish.location?.countryName}`}
            />
            <Box className={classes.photoContainer}>
              {dish.featuredPhoto && (
                <CustomImg
                  alt={dish.displayName}
                  className={classes.photo}
                  size="lg"
                  src={dish.featuredPhoto.photoUrl}
                  waitForScrolled={false}
                />
              )}
            </Box>
            <Box className={classes.infoContainer}>
              <Box>
                <DishLikesButton popsCount={dish.popsCount} />
                <DishShareButton shareUrl={shareUrl} />
              </Box>
              <DishModalAvatars avatars={dish.recentUserAvatars} />
              <DishModalDishTitle dish={dish} />
              <DishModalDishDetails
                dish={dish}
                placeSlug={placeSlug}
              />
              <DishModalReviews
                dishListSlug={dishListSlug}
                menuItemId={dish.id}
                placeSlug={placeSlug}
                reviewsSummary={dish.generatedReviewsSummary}
              />
            </Box>
            {actionText && actionUrl && (
              <DishModalCTA
                actionText={actionText}
                actionUrl={actionUrl}
              />
            )}
          </React.Fragment>
        )}
        <IconButton
          aria-label="Close Dialog"
          className={classes.closeButton}
          onClick={closeModal}
        >
          <Icon icon={XIcon} />
        </IconButton>
      </Box>
    </BasicModal>
  );
};
